/**
 * App colors and styling – single place to change the app's look and feel.
 *
 * To experiment with a different palette:
 * 1. Edit Brand.primary (and optionally Brand.primaryLight / Brand.primaryGradient) for the main accent.
 * 2. Edit Colors.light / Colors.dark for base text, background, and tab bar.
 * 3. Neutrals and primaryAlpha() drive the rest of the UI.
 *
 * See also: track-miles uses global.css + tailwind.config.js (CSS variables) and constants/theme.ts (design tokens).
 */

import { Platform } from 'react-native';

// ─── Brand (change these to get a different feel) ───
export const Brand = {
  /** Primary brand color (olive) */
  primary: '#708238',
  /** Accent & CTA – buttons, links, selected tab */
  accent: '#F4A460',
  primaryLight: '#c5d4a0',
  primaryLighter: '#e8ecd8',
  primaryGradient: ['#FAFAF9', '#f0f2e8', '#e8ecd8'] as const,
  primaryGradientStrong: ['#F4A460', '#e8945a'] as const,
  primaryDark: '#4d5a26',
  destructive: '#ef4444',
  destructiveLight: '#fee2e2',
} as const;

/** rgba(primary, alpha) – use for borders/backgrounds that tint with the brand. */
function hexToRgb(hex: string): string {
  const h = hex.replace(/^#/, '').slice(0, 6);
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
}
export const primaryAlpha = (alpha: number) => `rgba(${hexToRgb(Brand.primary)}, ${alpha})`;
export const accentAlpha = (alpha: number) => `rgba(${hexToRgb(Brand.accent)}, ${alpha})`;

// ─── Neutrals (text, borders, surfaces) ───
export const Neutrals = {
  textPrimary: '#1F2937',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  border: '#e5e7eb',
  borderLight: 'rgba(229, 231, 235, 0.6)',
  surface: '#f3f4f6',
  surfaceLight: '#f9fafb',
  cardBg: 'rgba(255, 255, 255, 0.7)',
  cardBgSolid: '#e5e7eb',
  overlay: 'rgba(0, 0, 0, 0.5)',
  white: '#fff',
  whiteFaded70: 'rgba(255, 255, 255, 0.7)',
  whiteFaded80: 'rgba(255, 255, 255, 0.8)',
  whiteFaded85: 'rgba(255, 255, 255, 0.85)',
  whiteFaded90: 'rgba(255, 255, 255, 0.9)',
  destructiveMuted: '#dc2626',
  destructiveDark: '#991b1b',
  destructiveBorder: '#fecaca',
  destructiveSoft: '#fca5a5',
} as const;

// ─── Themed colors (light/dark) – used by ThemedView, ThemedText, useThemeColor, tab bar ───
export const Colors = {
  light: {
    text: '#1F2937',
    background: '#FAFAF9',
    tint: Brand.accent,
    icon: '#687076',
    tabIconDefault: '#6B7280',
    tabIconSelected: Brand.accent,
    tabBarBackground: '#FAFAF9',
    tabBarActiveBackground: '#fef3e8',
  },
  dark: {
    text: '#11181C',
    background: '#fff',
    tint: Brand.primary,
    icon: '#687076',
    tabIconDefault: '#6B7280',
    tabIconSelected: Brand.primary,
    tabBarBackground: '#fff',
    tabBarActiveBackground: '#EDF8E5',
  },
  // dark mode is currently disabled to focus on core features, but this is a starting point for when we re-enable it. The light/dark colors are pretty similar right now, but we can adjust them as needed when we bring dark mode back.
  // dark: {
  //   text: '#ECEDEE',
  //   background: '#151718',
  //   tint: Brand.primary,
  //   icon: '#9BA1A6',
  //   tabIconDefault: '#9BA1A6',
  //   tabIconSelected: Brand.primary,
  //   tabBarBackground: '#151718',
  //   tabBarActiveBackground: 'transparent',
  // },
};

export const Fonts = Platform.select({
  ios: {
    /** iOS `UIFontDescriptorSystemDesignDefault` */
    sans: 'system-ui',
    /** iOS `UIFontDescriptorSystemDesignSerif` */
    serif: 'ui-serif',
    /** iOS `UIFontDescriptorSystemDesignRounded` */
    rounded: 'ui-rounded',
    /** iOS `UIFontDescriptorSystemDesignMonospaced` */
    mono: 'ui-monospace',
  },
  default: {
    sans: 'normal',
    serif: 'serif',
    rounded: 'normal',
    mono: 'monospace',
  },
  web: {
    sans: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    serif: "Georgia, 'Times New Roman', serif",
    rounded: "'SF Pro Rounded', 'Hiragino Maru Gothic ProN', Meiryo, 'MS PGothic', sans-serif",
    mono: "SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
});
